import { getDefaultBuConfig } from "domain/repository/apiUtil";
import { ChargeWaiverConstant } from "presentation/constant/ChargeWaiver/ChargeWaiverConstant";
import { useChargeWaiverVM } from "presentation/hook/ChargeWaiver/useChargeWaiverVM";
import { useChargeWaiverTracked } from "presentation/store/ChargeWaiver/ChargeWaiverProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useMemo } from "react";
import { FieldType, IFieldValue } from "veronica-ui-component/dist/component/core";

const ChargeWaiverSearchForm = () => {
    const CHARGE_WAIVER_CONSTANT = ChargeWaiverConstant.Search;
    const [chargeWaiverState] = useChargeWaiverTracked();
    const chargeWaiverSearchCriteria = chargeWaiverState.searchCriteria;
    const chargeWaiverVM = useChargeWaiverVM();
    const blNoMaxLength: number = getDefaultBuConfig<number>("REACT_APP_BILL_OF_LADING_MAX_LENGTH") || 35;

    const memoCntrNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "430px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                helpTextElement={"Use line break ( Enter ) as delimiter"}
                readOnlyValue={chargeWaiverSearchCriteria.cntrList?.join('\n') || ''}
                fieldValue={chargeWaiverSearchCriteria?.cntrList?.join('\n')}
                fieldLabel={CHARGE_WAIVER_CONSTANT.CONTAINER_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXTAREA}
                fieldKey={'cntrList'}
                maxLength={780}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    chargeWaiverVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [chargeWaiverSearchCriteria.cntrList, chargeWaiverVM, CHARGE_WAIVER_CONSTANT.CONTAINER_NO])

    const memoVesselCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={chargeWaiverSearchCriteria?.vesselCode || ''}
                fieldValue={chargeWaiverSearchCriteria?.vesselCode || ''}
                fieldLabel={CHARGE_WAIVER_CONSTANT.VESSEL_CODE}
                fieldType={FieldType.TEXT}
                fieldKey={'vesselCode'}
                isShowMissingError={true}
                maxLength={20}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    chargeWaiverVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [chargeWaiverSearchCriteria?.vesselCode, CHARGE_WAIVER_CONSTANT.VESSEL_CODE, chargeWaiverVM])

    const memoVoyageCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={chargeWaiverSearchCriteria?.voyageCode || ''}
                fieldValue={chargeWaiverSearchCriteria?.voyageCode || ''}
                fieldLabel={CHARGE_WAIVER_CONSTANT.VOYAGE_CODE}
                fieldType={FieldType.TEXT}
                fieldKey={'voyageCode'}
                isShowMissingError={true}
                maxLength={20}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    chargeWaiverVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [chargeWaiverSearchCriteria?.voyageCode, CHARGE_WAIVER_CONSTANT.VOYAGE_CODE, chargeWaiverVM])

    const memoConsortiumCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={chargeWaiverSearchCriteria?.consortiumCode || ''}
                fieldValue={chargeWaiverSearchCriteria?.consortiumCode}
                fieldLabel={CHARGE_WAIVER_CONSTANT.CONSORTIUM_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'consortiumCode'}
                maxLength={60}
                options={chargeWaiverState.dynamicOptions.consortiumCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    chargeWaiverVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [CHARGE_WAIVER_CONSTANT.CONSORTIUM_CODE, chargeWaiverState.dynamicOptions.consortiumCodeDropdownOptions, chargeWaiverVM, chargeWaiverSearchCriteria?.consortiumCode])

    const memoWaiverReason = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={chargeWaiverSearchCriteria?.waiverReason || ''}
                fieldValue={chargeWaiverSearchCriteria?.waiverReason}
                fieldLabel={CHARGE_WAIVER_CONSTANT.WAIVER_REASON}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'waiverReason'}
                maxLength={60}
                options={chargeWaiverState.dynamicOptions.waiverReasonDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    chargeWaiverVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [CHARGE_WAIVER_CONSTANT.WAIVER_REASON, chargeWaiverState.dynamicOptions.waiverReasonDropdownOptions, chargeWaiverVM, chargeWaiverSearchCriteria?.waiverReason])


    const memoEtd = useMemo(() =>
        <div className='im-flex-row-item'>
            <DatePickerComponent
                disabled={false}
                label={CHARGE_WAIVER_CONSTANT.ETD}
                width="200px"
                date={chargeWaiverSearchCriteria.etd}
                fieldName="etd"
                onDateChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    chargeWaiverVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [CHARGE_WAIVER_CONSTANT.ETD, chargeWaiverSearchCriteria.etd, chargeWaiverVM])

    const memoShippingBill = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={chargeWaiverSearchCriteria?.shippingBill || ''}
                fieldValue={chargeWaiverSearchCriteria?.shippingBill || ''}
                fieldLabel={CHARGE_WAIVER_CONSTANT.SHIPPING_BILL}
                fieldType={FieldType.TEXT}
                fieldKey={'shippingBill'}
                isShowMissingError={true}
                maxLength={blNoMaxLength}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    chargeWaiverVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [chargeWaiverSearchCriteria?.shippingBill, CHARGE_WAIVER_CONSTANT.SHIPPING_BILL, blNoMaxLength, chargeWaiverVM])

    return <>

        <CriteriaItemContainer>
            {memoCntrNo}
        </CriteriaItemContainer>

        <CriteriaItemContainer>
            {memoVesselCode}
            {memoVoyageCode}
        </CriteriaItemContainer>

        <CriteriaItemContainer>
            {memoConsortiumCode}
            {memoWaiverReason}
        </CriteriaItemContainer>

        <CriteriaItemContainer>
            {memoEtd}
            {memoShippingBill}
        </CriteriaItemContainer>

    </>;
}
export default memo(ChargeWaiverSearchForm);